import { useTranslation } from "react-i18next";

import { IProductionDashboardKpis } from "../../models/ProductionDashboard";
import { BayCardState } from "../../utils/bayCardState";
import classNames from "../../utils/classNames";
import { bayStateLabel } from "./bayStateStyles";
import OverviewKpiCard from "./OverviewKpiCard";
import PartsRemovedKpiCard from "./PartsRemovedKpiCard";

interface BaySummaryTilesProps {
  kpis: IProductionDashboardKpis;
  bayStates: BayCardState[];
  completedTodayCount: number;
}

const ATTENTION_ORDER: BayCardState[] = ["over", "warning", "waiting", "paused"];
const RUNNING: BayCardState[] = ["over", "warning", "working"];

// Deep link into the vehicles list with its Crushed chip preselected (see VehicleList).
export const CRUSHED_VEHICLES_LINK = "/vehicles?filter=crushed";

// The Bays-view stat block. Bays In Use comes from the API like the Line view's; the two counts
// beside it are worked out from the same states that colour the cards, so they can't disagree.
export default function BaySummaryTiles({ kpis, bayStates, completedTodayCount }: BaySummaryTilesProps) {
  const { t } = useTranslation("production");

  const runningCount = bayStates.filter((s) => RUNNING.includes(s)).length;
  const attentionBreakdown = ATTENTION_ORDER.map((state) => ({
    state,
    count: bayStates.filter((s) => s === state).length,
  })).filter((entry) => entry.count > 0);
  const attentionCount = attentionBreakdown.reduce((sum, entry) => sum + entry.count, 0);

  const { used, total } = kpis.baysInUse;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <OverviewKpiCard
        label={t("Bays In Use")}
        value={used}
        subValue={`/ ${total}`}
        footer={
          total > 0 ? (
            <div className="flex gap-0.5" aria-hidden="true">
              {Array.from({ length: total }, (_, i) => (
                <span
                  key={i}
                  className={classNames(
                    "h-1.5 flex-1 rounded-sm",
                    i < used ? "bg-emerald-500" : "bg-gray-200"
                  )}
                />
              ))}
            </div>
          ) : (
            t("Bays in use")
          )
        }
      />
      <OverviewKpiCard
        label={t("Timers Running")}
        value={runningCount}
        footer={t("Bays with someone clocked on")}
      />
      <OverviewKpiCard
        label={t("Needs Attention")}
        value={attentionCount}
        tone={attentionCount > 0 ? "warning" : "default"}
        footer={
          attentionBreakdown.length > 0
            ? attentionBreakdown
                .map((entry) => `${entry.count} ${t(bayStateLabel(entry.state)).toLowerCase()}`)
                .join(" · ")
            : t("Every bay is working or empty")
        }
      />
      <OverviewKpiCard
        label={t("Vehicles In Progress")}
        value={kpis.vehiclesInProgress}
        footer={t("Across all stations")}
      />
      <OverviewKpiCard
        label={t("Vehicles Dismantled Today")}
        value={completedTodayCount}
        footer={t("Completed jobs")}
      />
      <OverviewKpiCard
        label={t("Depolluted Today")}
        value={kpis.vehiclesDepollutedToday ?? 0}
        footer={t("Depollution finished today")}
      />
      <OverviewKpiCard
        label={t("Crushed Today")}
        value={kpis.vehiclesCrushedToday ?? 0}
        footer={t("Open crushed vehicles")}
        to={CRUSHED_VEHICLES_LINK}
      />
      <PartsRemovedKpiCard partsRemoved={kpis.partsRemoved} />
    </div>
  );
}
